import React, { useState } from "react";
import { useForm } from "../../hooks/useForm";

const UnitForm = ({
  initialValues,
  onSubmit,
  onCancel,
  title,
  submitText,
  isEditing = false,
  existingUnits = [],
}) => {
  // Validation rules
  const validationRules = {
    unitName: (value) => {
      if (!value?.trim()) return "שם היחידה נדרש";
      return "";
    },
    parentUnit: (value, allValues) => {
      // Parent unit is optional, but if provided, must be valid
      if (value && value !== "" && value !== "-") {
        const parentExists = existingUnits.some(
          (unit) => unit.unitId === value
        );
        if (!parentExists) return "יחידה אב לא קיימת";
      }
      return "";
    },
    level: (value) => {
      if (value === undefined || value === null || value === "") {
        return "רמה נדרשת";
      }
      if (isNaN(value) || value < 0) {
        return "רמה חייבת להיות מספר חיובי";
      }
      return "";
    },
    status: (value) => {
      if (!value?.trim()) return "סטטוס נדרש";
      return "";
    },
  };

  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    validateForm,
    resetForm,
    setFieldValue,
    setFieldError,
  } = useForm(initialValues, validationRules);

  // Check if there's already a level 0 unit (only one allowed)
  const hasLevel0Unit = existingUnits.some((unit) => unit.level === 0);
  const willBeLevel0 =
    !values.parentUnit || values.parentUnit === "" || values.parentUnit === "-";

  const handleSubmit = (e) => {
    e.preventDefault();

    // Check for level 0 restriction (only one level 0 unit allowed)
    if (!isEditing && hasLevel0Unit && willBeLevel0) {
      setFieldError(
        "parentUnit",
        "רק יחידה אחת יכולה להיות ברמה 0. יש כבר יחידה ברמה 0 במערכת."
      );
      return;
    }

    if (validateForm()) {
      // Calculate level based on parent unit hierarchy
      let calculatedLevel = 0;
      if (
        values.parentUnit &&
        values.parentUnit !== "" &&
        values.parentUnit !== "-"
      ) {
        const parentUnit = existingUnits.find(
          (unit) => unit.unitId === values.parentUnit
        );
        if (parentUnit) {
          // Child level = parent level + 1
          calculatedLevel = parentUnit.level + 1;
        }
      } else {
        // No parent = level 0 (top level unit)
        calculatedLevel = 0;
      }

      const formData = {
        ...values,
        level: calculatedLevel,
        parentUnit:
          values.parentUnit === "" || values.parentUnit === "-"
            ? null
            : values.parentUnit,
      };

      onSubmit(formData);
    }
  };

  const handleCancel = () => {
    resetForm();
    onCancel();
  };

  const handleParentUnitChange = (e) => {
    const parentUnitId = e.target.value;
    setFieldValue("parentUnit", parentUnitId);

    // Auto-calculate level based on parent's level
    if (parentUnitId && parentUnitId !== "" && parentUnitId !== "-") {
      const parentUnit = existingUnits.find(
        (unit) => unit.unitId === parentUnitId
      );
      if (parentUnit) {
        // Child level = parent level + 1
        setFieldValue("level", parentUnit.level + 1);
      }
    } else {
      // No parent = level 0 (top level)
      setFieldValue("level", 0);
    }
  };

  // Get available parent units (exclude current unit if editing)
  const availableParentUnits = existingUnits.filter(
    (unit) => unit.unitId !== (isEditing ? initialValues.unitId : null)
  );

  return (
    <form onSubmit={handleSubmit} className="unit-form">
      <div className="form-group">
        <label htmlFor="unitName">שם היחידה:</label>
        <input
          type="text"
          id="unitName"
          name="unitName"
          value={values.unitName}
          onChange={handleChange}
          onBlur={handleBlur}
          required
          className={touched.unitName && errors.unitName ? "error-input" : ""}
          placeholder="הזינו את שם היחידה"
        />
        {touched.unitName && errors.unitName && (
          <div className="error-message">{errors.unitName}</div>
        )}
      </div>

      <div className="form-group">
        <label htmlFor="parentUnit">יחידה אב:</label>
        <select
          id="parentUnit"
          name="parentUnit"
          value={values.parentUnit || ""}
          onChange={handleParentUnitChange}
          onBlur={handleBlur}
          className={
            touched.parentUnit && errors.parentUnit ? "error-input" : ""
          }
        >
          <option value="" disabled={hasLevel0Unit && !isEditing}>
            {hasLevel0Unit && !isEditing
              ? "- (יחידה ראשית - לא זמין, יש כבר יחידה ברמה 0)"
              : "- (יחידה ראשית)"}
          </option>
          {availableParentUnits.map((unit) => (
            <option key={unit.unitId} value={unit.unitId}>
              {unit.unitName}
            </option>
          ))}
        </select>
        {touched.parentUnit && errors.parentUnit && (
          <div className="error-message">{errors.parentUnit}</div>
        )}
        <small className="form-help">
          {hasLevel0Unit && !isEditing
            ? "רק יחידה אחת יכולה להיות ברמה 0. בחר יחידה אב קיימת."
            : "בחר יחידה אב או השאר ריק ליחידה ראשית"}
        </small>
      </div>

      <div className="form-group">
        <label htmlFor="level">רמה:</label>
        <input
          type="number"
          id="level"
          name="level"
          value={values.level}
          onChange={handleChange}
          onBlur={handleBlur}
          required
          min="0"
          className={touched.level && errors.level ? "error-input" : ""}
          disabled
        />
        <small className="form-help">
          הרמה מחושבת אוטומטית: יחידה ראשית (0), ילד של יחידה ראשית (1), ילד של
          ילד (2) וכו'
        </small>
        {touched.level && errors.level && (
          <div className="error-message">{errors.level}</div>
        )}
      </div>

      <div className="form-group">
        <label htmlFor="status">סטטוס:</label>
        <select
          id="status"
          name="status"
          value={values.status}
          onChange={handleChange}
          onBlur={handleBlur}
          required
          className={touched.status && errors.status ? "error-input" : ""}
        >
          <option value="active">פעיל</option>
          <option value="inactive">לא פעיל</option>
        </select>
        {touched.status && errors.status && (
          <div className="error-message">{errors.status}</div>
        )}
      </div>

      <div className="form-actions">
        <button type="submit" className="save-btn">
          {submitText}
        </button>
        <button type="button" className="cancel-btn" onClick={handleCancel}>
          ביטול
        </button>
      </div>
    </form>
  );
};

export default UnitForm;
